import { useLocation, useNavigate } from 'react-router'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import {
  ListItemNavigationDirection,
  useListItemNavigation,
  useMinimizedModalsContext,
} from '../../../../hooks'
import { MINIMIZED_MODAL_SCOPE } from '../../../Modal'
import { EMAIL_PROFILE_NAVIGATION_STATE_KEY } from '../../Email.constants'

import { EMAIL_PROFILE_NAVIGATION_DIRECT_LINK_COUNTER } from './useEmailProfileNavigation.constants'

export function useEmailProfileNavigation({
  getProfilePathname,
  loadNavigation: loadNavigationApi,
  mailUuid,
}) {
  const location = useLocation()
  const navigate = useNavigate()

  const { confirmBeforeAction } = useMinimizedModalsContext()

  const navigationState =
    location.state?.[EMAIL_PROFILE_NAVIGATION_STATE_KEY] ?? null

  const isEnabled = Boolean(navigationState)

  const {
    anchor = null,
    filterValues,
    isWidgetsHidden: initialWidgetsHidden = false,
    ordering,
  } = navigationState ?? EMPTY_OBJECT

  async function loadNavigation({ options, uuid }) {
    return loadNavigationApi({
      data: {
        filters: filterValues ?? EMPTY_OBJECT,
        ordering: ordering ?? EMPTY_LIST,
      },
      options,
      uuid,
    })
  }

  const { index, isLoaded, nextUuid, previousUuid, total } =
    useListItemNavigation({
      anchor,
      isEnabled,
      loadNavigation,
      uuid: mailUuid,
    })

  function goToEmail({ direction, isWidgetsHidden, uuid }) {
    if (uuid) {
      confirmBeforeAction(
        () =>
          navigate(
            {
              pathname: getProfilePathname({ id: uuid }),
              search: location.search,
            },
            {
              replace: true,
              state: {
                [EMAIL_PROFILE_NAVIGATION_STATE_KEY]: {
                  anchor: { direction, nextUuid, previousUuid, uuid: mailUuid },
                  filterValues,
                  isWidgetsHidden,
                  ordering,
                },
              },
            }
          ),
        { scope: MINIMIZED_MODAL_SCOPE.EMAIL_PROFILE }
      )
    }
  }

  function handlePrevious({
    isWidgetsHidden = initialWidgetsHidden,
  } = EMPTY_OBJECT) {
    goToEmail({
      direction: ListItemNavigationDirection.Previous,
      isWidgetsHidden,
      uuid: previousUuid,
    })
  }

  function handleNext({
    isWidgetsHidden = initialWidgetsHidden,
  } = EMPTY_OBJECT) {
    goToEmail({
      direction: ListItemNavigationDirection.Next,
      isWidgetsHidden,
      uuid: nextUuid,
    })
  }

  return {
    ...(isEnabled
      ? { index, total }
      : EMAIL_PROFILE_NAVIGATION_DIRECT_LINK_COUNTER),
    handleNext,
    handlePrevious,
    hasNext: Boolean(nextUuid),
    hasPrevious: Boolean(previousUuid),
    initialWidgetsHidden,
    isEnabled,
    isLoading: isEnabled && !isLoaded,
  }
}
